/**
 * 底部面板组件 - 适配扑克牌竖版布局
 * 
 * 手牌区域改为水平排列的高窄卡牌
 */

import React from 'react';
import { Eye, RefreshCw } from 'lucide-react';
import type { GameCard } from '../../../types';
import type { GamePhase } from '../../../stores/gameStore';
import { MAX_HAND_SIZE, MAX_SWAPS } from '../../../constants';
import { HandCard } from '../cards/HandCard';

interface BottomPanelProps {
  cards: GameCard[];
  deck: GameCard[];
  phase: GamePhase;
  swapCount: number;
  onSkip: () => void;
  onSwap: () => void;
  onSelectCard: (card: GameCard) => void;
}

export const BottomPanel: React.FC<BottomPanelProps> = React.memo(({
  cards,
  deck,
  phase,
  swapCount,
  onSkip,
  onSwap,
  onSelectCard,
}) => {
  const isPlayerTurn = phase === 'PLAYER_TURN';
  const canSwap = isPlayerTurn && swapCount < MAX_SWAPS && deck.length > 0;
  // Only show swap if there are deck cards not already in hand
  const deckCardsNotInHand = deck.filter(d => !cards.some(c => c.id === d.id));
  const hasSwappableCards = deckCardsNotInHand.length > 0;

  return (
    <div className="bg-white/95 backdrop-blur-md border-t border-[#E5E0D8] p-3 shrink-0 safe-area-bottom shadow-lg">
      {/* Deck Info */}
      <div className="flex justify-end items-center mb-3 px-1">
        {/* 牌组信息 */}
        <div className="flex items-center gap-2 text-[10px] text-[#6B7280]">
          <span className="px-2 py-1 bg-[#F5EDE3] rounded-full font-medium">
            牌堆 {deck.length}
          </span>
          <span className="px-2 py-1 bg-[#F5EDE3] rounded-full font-medium">
            手牌 {cards.length}/{MAX_HAND_SIZE}
          </span>
          <span className="px-2 py-1 bg-[#9B8EC4]/10 text-[#9B8EC4] rounded-full font-medium">
            换牌 {MAX_SWAPS - swapCount}/{MAX_SWAPS}
          </span>
        </div>
      </div>

      {/* 手牌区域: 静观其变 + 换牌 + 卡牌列表 - 水平排列 */}
      <div className="relative">
        {/* 右侧渐变遮罩 - 滚动提示 */}
        <div className="absolute right-0 top-0 bottom-0 w-6 bg-gradient-to-l from-white/90 to-transparent z-10 pointer-events-none sm:hidden" />
      <div className="flex items-stretch gap-2 px-2 overflow-x-auto pb-1 sm:justify-center">
        {/* 静观其变按钮 */}
        <ObserveButton 
          disabled={!isPlayerTurn} 
          onClick={onSkip} 
        />

        {/* 换牌按钮 */}
        <SwapButton
          disabled={!canSwap || !hasSwappableCards}
          swapsRemaining={MAX_SWAPS - swapCount}
          onClick={onSwap}
        />

        {/* 手牌列表 */}
        {cards.map((card) => (
          <HandCard
            key={card.id}
            card={card}
            isClickable={isPlayerTurn}
            onClick={() => onSelectCard(card)}
          />
        ))}
      </div>
      </div>
    </div>
  );
});

BottomPanel.displayName = 'BottomPanel';

/**
 * 静观其变按钮组件 - 扑克牌比例
 */
interface ObserveButtonProps {
  disabled: boolean;
  onClick: () => void;
}

const ObserveButton: React.FC<ObserveButtonProps> = ({ disabled, onClick }) => {
  return (
    <button
      disabled={disabled}
      onClick={onClick}
      className={`
        relative
        w-[80px] sm:w-[100px]
        aspect-[2/3.2]
        flex-shrink-0
        bg-gradient-to-br from-[#F5EDE3] via-[#FAF6F1] to-[#EDE8F5]
        border-2 border-[#D4C8B8]
        rounded-lg
        shadow-md
        overflow-hidden
        flex flex-col
        transition-all duration-300 ease-out
        ${disabled 
          ? 'opacity-50 cursor-not-allowed' 
          : 'hover:shadow-lg hover:-translate-y-1 cursor-pointer active:scale-95'
        }
        group
      `}
    >
      {/* 背景装饰 */}
      <div className="absolute inset-0 opacity-[0.05] pointer-events-none">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 50% 50%, #9B8EC4 0.5px, transparent 0.5px)`,
          backgroundSize: '10px 10px',
        }} />
      </div>

      {/* 顶部区域 */}
      <div className="p-1.5 sm:p-2 flex justify-center">
        <div className={`
          w-5 h-5 sm:w-6 sm:h-6
          bg-[#9B8EC4]/30
          rounded-full
          flex items-center justify-center
          border border-[#9B8EC4]/40
        `}>
          <span className="text-[10px] text-[#9B8EC4] font-bold">—</span>
        </div>
      </div>

      {/* 中部图标 */}
      <div className="flex-1 flex items-center justify-center">
        <div className={`
          w-9 h-9 sm:w-14 sm:h-14
          bg-[#9B8EC4]/20
          rounded-xl sm:rounded-2xl
          flex items-center justify-center
          border-2 border-[#9B8EC4]/30
          shadow-md
          ${!disabled ? 'group-hover:scale-110 group-hover:bg-[#9B8EC4]/30' : ''}
          transition-all duration-300
        `}>
          <Eye className="w-5 h-5 sm:w-7 sm:h-7 text-[#9B8EC4]" strokeWidth={2} />
        </div>
      </div>

      {/* 底部文字 */}
      <div className="p-1 sm:p-2 flex flex-col items-center gap-0.5 sm:gap-1">
        <span className="text-[10px] sm:text-xs font-bold text-[#6B5B4F]">
          静观其变
        </span>
        <span className="text-[8px] sm:text-[9px] text-[#9CA3AF]">
          跳过本轮
        </span>
      </div>

      {/* 装饰线 */}
      <div className="absolute top-0 left-2 right-2 h-0.5 bg-gradient-to-r from-transparent via-[#9B8EC4]/30 to-transparent" />
      <div className="absolute bottom-0 left-2 right-2 h-0.5 bg-gradient-to-r from-transparent via-[#9B8EC4]/30 to-transparent" />
    </button>
  );
};

/**
 * 换牌按钮组件 - 扑克牌比例
 */
interface SwapButtonProps {
  disabled: boolean;
  swapsRemaining: number;
  onClick: () => void;
}

const SwapButton: React.FC<SwapButtonProps> = ({ disabled, swapsRemaining, onClick }) => {
  return (
    <button
      disabled={disabled}
      onClick={onClick}
      className={`
        relative
        w-[80px] sm:w-[100px]
        aspect-[2/3.2]
        flex-shrink-0
        bg-gradient-to-br from-[#E8F5E9] via-[#F1F8F2] to-[#E3F2FD]
        border-2 border-[#A5D6A7]
        rounded-lg
        shadow-md
        overflow-hidden
        flex flex-col
        transition-all duration-300 ease-out
        ${disabled 
          ? 'opacity-50 cursor-not-allowed' 
          : 'hover:shadow-lg hover:-translate-y-1 cursor-pointer active:scale-95'
        }
        group
      `}
    >
      {/* 背景装饰 */}
      <div className="absolute inset-0 opacity-[0.05] pointer-events-none">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 50% 50%, #4CAF50 0.5px, transparent 0.5px)`,
          backgroundSize: '10px 10px',
        }} />
      </div>

      {/* 顶部区域 */}
      <div className="p-1.5 sm:p-2 flex justify-center">
        <div className="w-5 h-5 sm:w-6 sm:h-6 bg-[#4CAF50]/30 rounded-full flex items-center justify-center border border-[#4CAF50]/40">
          <span className="text-[10px] text-[#4CAF50] font-bold">{swapsRemaining}</span>
        </div>
      </div>

      {/* 中部图标 */}
      <div className="flex-1 flex items-center justify-center">
        <div className={`
          w-9 h-9 sm:w-14 sm:h-14
          bg-[#4CAF50]/20
          rounded-xl sm:rounded-2xl
          flex items-center justify-center
          border-2 border-[#4CAF50]/30
          shadow-md
          ${!disabled ? 'group-hover:scale-110 group-hover:bg-[#4CAF50]/30' : ''}
          transition-all duration-300
        `}>
          <RefreshCw className="w-5 h-5 sm:w-7 sm:h-7 text-[#4CAF50]" strokeWidth={2} />
        </div>
      </div>

      {/* 底部文字 */}
      <div className="p-1 sm:p-2 flex flex-col items-center gap-0.5 sm:gap-1">
        <span className="text-[10px] sm:text-xs font-bold text-[#2E7D32]">
          换牌
        </span>
        <span className="text-[8px] sm:text-[9px] text-[#9CA3AF]">
          从牌堆选牌
        </span>
      </div>

      {/* 装饰线 */}
      <div className="absolute top-0 left-2 right-2 h-0.5 bg-gradient-to-r from-transparent via-[#4CAF50]/30 to-transparent" />
      <div className="absolute bottom-0 left-2 right-2 h-0.5 bg-gradient-to-r from-transparent via-[#4CAF50]/30 to-transparent" />
    </button>
  );
};
